import {
  ChangeDetectionStrategy,
  Component,
  createEnvironmentInjector,
  EnvironmentInjector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageChangeEvent, PaginatedSearchHeaderComponentModule } from '@shared/ui';
import { of, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { algorithmFacadeProviders } from './algorithms.facade.providers';
import {
  AlgorithmColumn,
  AlgorithmListComponent,
  PipelineStepAction,
} from '../../algorithms/list/algorithm-list.component';
import { AlgorithmsFacade } from './algorithms.facade';
import { AlgorithmData } from '../../../state/algorithm.state';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SwitchAlgorithmDialogComponent } from '../switch/switch-algorithm-dialog.component';
import { switchAlgorithmProviders } from '../switch/switch-algorithm.providers';
import { useLocalization } from '@shared/localization';
import { AlgorithmDeletionDialogComponent } from '../dialog/deletion-dialog/algorithm-deletion-dialog.component';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ExecutionEnvironment } from '@telespot/domain';

@Component({
    selector: 'ts-algorithms',
    templateUrl: './algorithms.component.html',
    styleUrls: ['./algorithms.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        AlgorithmListComponent,
        TranslateModule,
        PaginatedSearchHeaderComponentModule,
        MatFormFieldModule,
        MatSelectModule,
    ],
    providers: [...algorithmFacadeProviders]
})
export class AlgorithmsComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _switchAlgorithmDialogRef: MatDialogRef<SwitchAlgorithmDialogComponent> | undefined;
  private _deletionDialogRef: MatDialogRef<AlgorithmDeletionDialogComponent> | undefined;

  // Facade observables
  public algorithms$ = this.facade.algorithms$;
  public loading$ = this.facade.loadingAlgorithms$;
  public noAlgorithms$ = this.facade.emptyList$;
  public searchTerm$ = this.facade.algorithmStore.searchTerm$;
  public previousAlgorithmTokens$ = this.facade.algorithmStore.previousTokens$;
  public nextAlgorithmToken$ = this.facade.algorithmStore.nextToken$;
  public totalCount$ = this.facade.totalAlgorithms$;
  public pageSize$ = this.facade.algorithmStore.pageSize$;
  public algorithmsLoaded$ = this.facade.algorithmStore.itemsLoaded$;
  public counting$ = this.facade.algorithmStore.counting$;
  public usages$ = this.facade.algorithmUsageMap$;
  public environmentFilter$ = this.facade.algorithmStore.environmentFilter$;
  public includeDeprecated$ = this.facade.algorithmStore.includeDeprecated$;

  // Column configuration
  public columns = [
    AlgorithmColumn.NAME,
    AlgorithmColumn.VERSION,
    AlgorithmColumn.ORGANIZATIONS,
    AlgorithmColumn.WORKSPACES,
    AlgorithmColumn.REGULATED,
    AlgorithmColumn.ENVIRONMENT,
    AlgorithmColumn.STATUS,
    AlgorithmColumn.EDIT,
  ];
  public readonly pageSizeOptions = [10, 20, 50];
  public readonly ExecutionEnvironment = ExecutionEnvironment;

  constructor(
    private facade: AlgorithmsFacade,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private envInjector: EnvironmentInjector
  ) {}

  public readonly currentLanguage$ = useLocalization().currentLanguage$;

  ngOnInit() {
    this.facade.initializeAlgorithms();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Handles search term changes
   */
  public searchTermChanged(value: string): void {
    this.facade.algorithmStore.setSearchTerm(value);
  }

  /**
   * Handles environment filter changes
   */
  public environmentFilterChanged(env: ExecutionEnvironment | undefined): void {
    this.facade.setEnvironmentFilter(env);
  }

  /**
   * Handles include deprecated filter changes
   */
  public includeDeprecatedChanged(includeDeprecated: boolean): void {
    this.facade.setIncludeDeprecated(includeDeprecated);
  }

  /**
   * Handles page navigation
   */
  public handlePageEvent(event: PageChangeEvent<AlgorithmData>): void {
    this.facade.loadMoreAlgorithms(event.nextToken);
  }

  /**
   * Handles page size changes
   */
  public changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  /**
   * Handles deprecate action
   */
  onDeprecateClicked(algorithm: AlgorithmData): void {
    this.showDeprecateConfirmation(algorithm)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        error: (error) => {
          console.error(error);
          this.snackBar.open(error.message || error, null, { duration: 2000 });
        },
      });
  }

  private showSwitchAlgorithmDialog(action: PipelineStepAction) {
    const pipelineData = this.facade.getPipelineData(action.algorithmId, action.pipelineStepId);
    const dialogInjector = createEnvironmentInjector([...switchAlgorithmProviders], this.envInjector);
    const dialogRef =
      this._switchAlgorithmDialogRef ||
      this.dialog.open(SwitchAlgorithmDialogComponent, {
        data: {
          currentAlgorithmId: action.algorithmId,
          algorithmExecutionEnvironment: pipelineData.environment,
          pipelineId: pipelineData.id,
          pipelineName: pipelineData.name,
          pipelineUsage: Object.values(pipelineData.organizations),
          pipelineStepId: action.pipelineStepId,
        },
        injector: dialogInjector,
      });

    this._switchAlgorithmDialogRef = dialogRef;
    return dialogRef.afterClosed().pipe(
      tap(() => {
        this._switchAlgorithmDialogRef = undefined;
        dialogInjector.destroy();
      }),
      tap((ans) => {
        if (ans !== 'cancel') {
          this.facade.updatePipelineStepExecutor({
            pipelineStepId: action.pipelineStepId,
            executorId: ans,
            oldExecutorId: action.algorithmId,
          });
        }
      })
    );
  }

  /**
   * Handles edit pipeline step action
   */
  editAlgorithmInPipelineStep(action: PipelineStepAction): void {
    this.showSwitchAlgorithmDialog(action)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        error: (error) => {
          console.error(error);
          this.snackBar.open(error.message || error, null, { duration: 2000 });
        },
      });
  }

  /**
   * Shows confirmation dialog for removing an algorithm from a pipeline step
   */
  private showRemoveFromPipelineStepConfirmation(action: PipelineStepAction) {
    const pipelineData = this.facade.getPipelineData(action.algorithmId, action.pipelineStepId);
    const dialogRef =
      this._deletionDialogRef ||
      this.dialog.open(AlgorithmDeletionDialogComponent, {
        data: {
          pipelineUsage: Object.values(pipelineData.organizations),
          deprecationInformation: undefined,
          deletionInformation: {
            pipelineName: pipelineData.name,
            pipelineStepId: action.pipelineStepId,
          },
        },
      });

    this._deletionDialogRef = dialogRef;
    return dialogRef.afterClosed().pipe(
      tap(() => (this._deletionDialogRef = undefined)),
      tap((ans) => {
        if (ans === true) {
          this.facade.updatePipelineStepExecutor({
            pipelineStepId: action.pipelineStepId,
            executorId: undefined,
            oldExecutorId: action.algorithmId,
          });
        }
      })
    );
  }

  /**
   * Handles delete from pipeline step action
   */
  deleteAlgorithmFromPipelineStep(action: PipelineStepAction): void {
    this.showRemoveFromPipelineStepConfirmation(action)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        error: (error) => {
          console.error(error);
          this.snackBar.open(error.message || error, null, { duration: 2000 });
        },
      });
  }

  /**
   * Handles usage request for an algorithm
   */
  onUsageRequested(algorithmId: string): void {
    this.facade.listAlgorithmUsage(of(algorithmId));
  }

  /**
   * Shows confirmation dialog for deprecating an algorithm
   */
  private showDeprecateConfirmation(algorithm: AlgorithmData) {
    const algorithmData = this.facade.getCombinedPipelineData(algorithm.id);
    const dialogRef =
      this._deletionDialogRef ||
      this.dialog.open(AlgorithmDeletionDialogComponent, {
        data: {
          pipelineUsage: Object.values(algorithmData),
          deletionInformation: undefined,
          deprecationInformation: {
            algorithmName: algorithm.name,
            isDeprecated: algorithm.deprecated,
          },
        },
      });
    this._deletionDialogRef = dialogRef;
    return dialogRef.afterClosed().pipe(
      tap(() => (this._deletionDialogRef = undefined)),
      tap((ans) => {
        if (ans === true) {
          this.facade.changeAlgorithmDeprecationStatus(algorithm);
        }
      })
    );
  }

  /**
   * Handles navigate to pipeline action
   */
  navigateToPipeline(pipelineId: string): void {
    this.router.navigate(['/protocols/pipelines', pipelineId]);
  }

  /**
   * Handles edit algorithm action - navigates to algorithm editor
   */
  onEditClicked(algorithm: AlgorithmData): void {
    this.router.navigate(['/protocols/algorithms/edit', algorithm.id]);
  }
}
